import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { Trash2, Plus, Save, Loader2, Eye, EyeOff, ArrowUp, ArrowDown } from "lucide-react";
import FileUpload from "../../components/admin/FileUpload";
import { CATEGORIES } from "../../data/catalog";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Days between today (IST calendar) and an ISO YYYY-MM-DD date. Positive =
// upcoming expiry, 0 = expires today, negative = already expired.
function daysUntil(iso) {
  if (!iso) return null;
  const now = new Date();
  const istOffset = 5.5 * 60; // minutes
  const istNow = new Date(now.getTime() + (istOffset - now.getTimezoneOffset()) * 60000);
  const todayStr = istNow.toISOString().slice(0, 10);
  const a = new Date(`${todayStr}T00:00:00Z`);
  const b = new Date(`${iso}T00:00:00Z`);
  return Math.round((b - a) / (24 * 60 * 60 * 1000));
}

const EMPTY = {
  name: "",
  tagline: "",
  cover: "",
  enabled: true,
  priority: 0,
  designIds: [],
  endDate: "",
};

/**
 * Admin > Limited Collections tab.
 * CRUD for Raksha Bandhan / Diwali / Wedding-week spotlights.
 * Uses the same colour-coded design list the DesignsTab uses so admin
 * can pick pieces by name + purity + weight without leaving the page.
 */
export default function LimitedCollectionsTab() {
  const [items, setItems] = useState([]);
  const [form, setForm] = useState(EMPTY);
  const [editingId, setEditingId] = useState(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [designs, setDesigns] = useState([]);
  const [pickerQuery, setPickerQuery] = useState("");
  const [pickerCat, setPickerCat] = useState("all");

  const load = () =>
    axios
      .get(`${API}/admin/limited-collections`)
      .then((r) => setItems(r.data))
      .catch(() => {});

  useEffect(() => {
    load();
    // Pull the admin's own designs for the picker. Default catalog isn't
    // exposed as a flat list from the API — we let the admin add custom
    // designs here; default-catalog ids can still be pasted manually.
    axios.get(`${API}/admin/designs`).then((r) => setDesigns(r.data)).catch(() => {});
  }, []);

  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const startEdit = (row) => {
    setEditingId(row.id);
    setForm({
      name: row.name || "",
      tagline: row.tagline || "",
      cover: row.cover || "",
      enabled: !!row.enabled,
      priority: Number(row.priority) || 0,
      designIds: Array.isArray(row.designIds) ? row.designIds : [],
      endDate: row.endDate || "",
    });
    setError("");
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const resetForm = () => {
    setEditingId(null);
    setForm(EMPTY);
    setError("");
  };

  const save = async (e) => {
    e.preventDefault();
    if (!form.name.trim()) return setError("Give the collection a name");
    setSaving(true);
    try {
      if (editingId) {
        await axios.put(`${API}/admin/limited-collections/${editingId}`, form);
      } else {
        await axios.post(`${API}/admin/limited-collections`, form);
      }
      await load();
      resetForm();
    } catch (err) {
      setError(err?.response?.data?.detail || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this limited collection? Its designs are not deleted.")) return;
    await axios.delete(`${API}/admin/limited-collections/${id}`);
    if (editingId === id) resetForm();
    await load();
  };

  const toggleEnabled = async (row) => {
    await axios.put(`${API}/admin/limited-collections/${row.id}`, {
      ...row,
      enabled: !row.enabled,
    });
    await load();
  };

  const bumpPriority = async (row, delta) => {
    await axios.put(`${API}/admin/limited-collections/${row.id}`, {
      ...row,
      priority: Number(row.priority || 0) + delta,
    });
    await load();
  };

  const toggleDesign = (id) => {
    setForm((f) => {
      const inSet = f.designIds.includes(id);
      return {
        ...f,
        designIds: inSet ? f.designIds.filter((x) => x !== id) : [...f.designIds, id],
      };
    });
  };

  const filteredDesigns = useMemo(() => {
    const q = pickerQuery.trim().toLowerCase();
    return designs.filter((d) => {
      // Hide designs that are scoped to a DIFFERENT limited collection —
      // otherwise a Diwali editor would see Raksha Bandhan pieces in its picker.
      // But keep designs already bound to THIS collection so admin can untick them.
      if (d.limitedCollectionId && d.limitedCollectionId !== editingId) return false;
      if (pickerCat !== "all" && d.category !== pickerCat) return false;
      if (!q) return true;
      return (
        (d.name || "").toLowerCase().includes(q) ||
        (d.id || "").toLowerCase().includes(q)
      );
    });
  }, [designs, pickerQuery, pickerCat, editingId]);

  return (
    <div>
      <h1 className="admin-h1">Limited-time Collections</h1>
      <p className="admin-sub">
        Raksha Bandhan, Diwali, Wedding week — anything time-boxed. Enabled ones show
        <strong> at the very top </strong> of your homepage (above every other section).
        Lower <em>priority</em> shows first when multiple are enabled.
      </p>

      {/* ---------- Editor ---------- */}
      <form onSubmit={save} className="admin-form" data-testid="ltc-form">
        <h2 className="admin-h2">
          {editingId ? "Edit collection" : "New collection"}
        </h2>

        <div className="admin-grid-2">
          <label>
            <span>Name</span>
            <input
              type="text"
              value={form.name}
              onChange={(e) => update("name", e.target.value)}
              placeholder="Raksha Bandhan 2026"
              maxLength={80}
              data-testid="ltc-name"
              required
            />
          </label>
          <label>
            <span>Priority (lower = shown first)</span>
            <input
              type="number"
              value={form.priority}
              onChange={(e) => update("priority", Number(e.target.value) || 0)}
              data-testid="ltc-priority"
            />
          </label>
        </div>

        <label>
          <span>
            Auto-hide after this date{" "}
            <em style={{ color: "#8A5427", fontStyle: "normal", fontSize: 12 }}>
              (optional — leave blank to keep it live indefinitely)
            </em>
          </span>
          <input
            type="date"
            value={form.endDate || ""}
            onChange={(e) => update("endDate", e.target.value)}
            data-testid="ltc-end-date"
            style={{ maxWidth: 220 }}
          />
        </label>

        <label>
          <span>Tagline (short subheading)</span>
          <input
            type="text"
            value={form.tagline}
            onChange={(e) => update("tagline", e.target.value)}
            placeholder="Hand-picked rakhi-day pieces to gift or wear together."
            maxLength={160}
            data-testid="ltc-tagline"
          />
        </label>

        <label>
          <span>Cover image (optional — shown next to the heading)</span>
          <FileUpload
            value={form.cover}
            onChange={(v) => update("cover", v)}
            accept="image/*"
            data-testid="ltc-cover"
          />
        </label>

        <label className="admin-inline">
          <input
            type="checkbox"
            checked={form.enabled}
            onChange={(e) => update("enabled", e.target.checked)}
            data-testid="ltc-enabled"
          />
          <span>Live on the site</span>
          <em style={{ marginLeft: 8, color: "#8A5427", fontStyle: "normal", fontSize: 12 }}>
            (uncheck to prepare a collection without publishing yet)
          </em>
        </label>

        {/* ---------- Design picker ---------- */}
        {editingId && (
          <ScopedDesignCreator
            collectionId={editingId}
            onCreated={(newDesign) => {
              // Add to the local designs pool + auto-tick it in this collection
              setDesigns((prev) => [newDesign, ...prev]);
              setForm((f) => ({ ...f, designIds: [...f.designIds, newDesign.id] }));
            }}
          />
        )}

        <div className="ltc-picker">
          <div className="ltc-picker-head">
            <strong>Designs in this collection ({form.designIds.length})</strong>
            <div className="ltc-picker-filters">
              <input
                type="text"
                placeholder="Search your designs…"
                value={pickerQuery}
                onChange={(e) => setPickerQuery(e.target.value)}
                data-testid="ltc-picker-search"
              />
              <select
                value={pickerCat}
                onChange={(e) => setPickerCat(e.target.value)}
                data-testid="ltc-picker-cat"
              >
                <option value="all">Every category</option>
                {CATEGORIES.map((c) => (
                  <option key={c.slug} value={c.slug}>{c.label}</option>
                ))}
              </select>
            </div>
          </div>

          {designs.length === 0 ? (
            <p className="ltc-picker-empty">
              You haven't added any custom designs yet. Head to <strong>Designs</strong> tab and
              create at least one, then come back to add it here.
            </p>
          ) : (
            <div className="ltc-picker-grid" data-testid="ltc-picker-grid">
              {filteredDesigns.map((d) => {
                const on = form.designIds.includes(d.id);
                return (
                  <button
                    key={d.id}
                    type="button"
                    onClick={() => toggleDesign(d.id)}
                    className={`ltc-picker-item ${on ? "on" : ""}`}
                    data-testid={`ltc-picker-item-${d.id}`}
                  >
                    <span className="ltc-picker-check">{on ? "✓" : ""}</span>
                    <strong>{d.name}</strong>
                    <em>
                      {d.category} · {d.subCategory} · {d.purity} · {d.weight}{d.weightUnit || "g"}
                    </em>
                  </button>
                );
              })}
              {filteredDesigns.length === 0 && (
                <p className="ltc-picker-empty">No matches for that search.</p>
              )}
            </div>
          )}
        </div>

        {error && <p className="admin-error" data-testid="ltc-error">{error}</p>}

        <div className="admin-actions">
          <button
            type="submit"
            className="btn btn-primary"
            disabled={saving}
            data-testid="ltc-save"
          >
            {saving ? <Loader2 size={14} className="spin" /> : <Save size={14} />}
            <span style={{ marginLeft: 6 }}>{editingId ? "Update" : "Create"}</span>
          </button>
          {editingId && (
            <button
              type="button"
              className="btn btn-outline"
              onClick={resetForm}
              data-testid="ltc-cancel"
            >
              Cancel
            </button>
          )}
        </div>
      </form>

      {/* ---------- Existing list ---------- */}
      <h2 className="admin-h2" style={{ marginTop: 40 }}>
        Existing collections ({items.length})
      </h2>
      {items.length === 0 ? (
        <p className="admin-muted">Nothing here yet — create your first collection above.</p>
      ) : (
        <ul className="admin-list" data-testid="ltc-list">
          {items.map((row) => (
            <li key={row.id} data-testid={`ltc-list-${row.id}`}>
              <div className="admin-list-body">
                <strong>
                  {row.name}
                  {(() => {
                    const dLeft = daysUntil(row.endDate);
                    if (!row.enabled) return <span className="ltc-pill ltc-pill-off">HIDDEN</span>;
                    if (dLeft !== null && dLeft < 0)
                      return <span className="ltc-pill ltc-pill-expired">EXPIRED</span>;
                    return <span className="ltc-pill ltc-pill-on">LIVE</span>;
                  })()}
                  {row.endDate && (() => {
                    const dLeft = daysUntil(row.endDate);
                    if (dLeft === null) return null;
                    if (dLeft < 0) return <span className="ltc-pill ltc-pill-muted">ended {-dLeft}d ago</span>;
                    if (dLeft === 0) return <span className="ltc-pill ltc-pill-warn">ends today</span>;
                    if (dLeft <= 3) return <span className="ltc-pill ltc-pill-warn">{dLeft}d left</span>;
                    return <span className="ltc-pill ltc-pill-muted">{dLeft}d left</span>;
                  })()}
                </strong>
                <span>
                  Priority {row.priority} · {row.designIds?.length || 0} designs
                  {row.endDate ? ` · auto-hides ${row.endDate}` : ""}
                  {row.tagline ? ` · "${row.tagline.slice(0, 60)}${row.tagline.length > 60 ? "…" : ""}"` : ""}
                </span>
              </div>
              <div className="admin-list-actions">
                <button
                  type="button"
                  onClick={() => bumpPriority(row, -1)}
                  aria-label="Move up in order"
                  data-testid={`ltc-up-${row.id}`}
                >
                  <ArrowUp size={14} />
                </button>
                <button
                  type="button"
                  onClick={() => bumpPriority(row, 1)}
                  aria-label="Move down in order"
                  data-testid={`ltc-down-${row.id}`}
                >
                  <ArrowDown size={14} />
                </button>
                <button
                  type="button"
                  onClick={() => toggleEnabled(row)}
                  aria-label={row.enabled ? "Hide from site" : "Publish live"}
                  data-testid={`ltc-toggle-${row.id}`}
                >
                  {row.enabled ? <EyeOff size={14} /> : <Eye size={14} />}
                </button>
                <button
                  type="button"
                  onClick={() => startEdit(row)}
                  data-testid={`ltc-edit-${row.id}`}
                >
                  Edit
                </button>
                <button
                  type="button"
                  className="danger"
                  onClick={() => remove(row.id)}
                  aria-label="Delete"
                  data-testid={`ltc-delete-${row.id}`}
                >
                  <Trash2 size={14} />
                </button>
              </div>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}


// ---------------------------------------------------------------------------
// Inline "create a design exclusively for this collection" mini-form.
// Bound designs are stored in custom_designs with limitedCollectionId set —
// backend excludes them from category/style pages, so they only appear here.
// ---------------------------------------------------------------------------
const SCOPED_EMPTY = {
  name: "",
  image: "",
  image2: "",
  video: "",
  weight: "",
  weightUnit: "g",
  purity: "22k",
  gender: "unisex",
  makingCharge: "On request",
  hallmark: "BIS Hallmark 916",
  description: "",
};

function ScopedDesignCreator({ collectionId, onCreated }) {
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(SCOPED_EMPTY);
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState("");

  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const save = async () => {
    if (!form.name.trim()) return setErr("Give it a name");
    if (!form.image) return setErr("Add an image so shoppers see the piece");
    setErr("");
    setSaving(true);
    try {
      // A limited-collection design still needs a category/subCategory for
      // the internal schema — we tag it under "gold / rings" as a neutral
      // default, but it's hidden from those pages via limitedCollectionId.
      // Extra image (image2) goes into the standard gallery so the design
      // detail page + card can show both without touching other schemas.
      const gallery = form.image2 ? [form.image2] : [];
      const payload = {
        name: form.name,
        image: form.image,
        video: form.video,
        gallery,
        weight: Number(form.weight) || 0,
        weightUnit: form.weightUnit,
        purity: form.purity,
        gender: form.gender,
        makingCharge: form.makingCharge,
        hallmark: form.hallmark,
        description: form.description,
        category: "gold",
        subCategory: "rings",
        styleName: "",
        isExclusive: false,
        limitedCollectionId: collectionId,
      };
      const { data } = await axios.post(`${API}/admin/designs`, payload);
      onCreated(data);
      setForm(SCOPED_EMPTY);
      setOpen(false);
    } catch (e) {
      setErr(e?.response?.data?.detail || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  if (!open) {
    return (
      <button
        type="button"
        className="btn btn-outline"
        onClick={() => setOpen(true)}
        data-testid="ltc-scoped-open"
        style={{ alignSelf: "flex-start" }}
      >
        <Plus size={14} /> <span style={{ marginLeft: 6 }}>Create a design exclusively for this collection</span>
      </button>
    );
  }

  return (
    <div className="ltc-scoped" data-testid="ltc-scoped-form">
      <div className="ltc-scoped-head">
        <strong>New design (only appears inside this collection)</strong>
        <button
          type="button"
          className="btn btn-outline"
          onClick={() => { setOpen(false); setForm(SCOPED_EMPTY); setErr(""); }}
          style={{ padding: "4px 10px", fontSize: 12 }}
          data-testid="ltc-scoped-cancel"
        >
          Cancel
        </button>
      </div>

      <div className="admin-grid-2">
        <label>
          <span>Name</span>
          <input
            type="text"
            value={form.name}
            onChange={(e) => update("name", e.target.value)}
            placeholder="Rakhi Special Bracelet"
            maxLength={140}
            data-testid="ltc-scoped-name"
          />
        </label>
        <label>
          <span>Weight (grams)</span>
          <input
            type="number"
            step="0.01"
            value={form.weight}
            onChange={(e) => update("weight", e.target.value)}
            placeholder="12.5"
            data-testid="ltc-scoped-weight"
          />
        </label>
      </div>

      <div className="admin-grid-2">
        <label>
          <span>Purity</span>
          <input
            type="text"
            value={form.purity}
            onChange={(e) => update("purity", e.target.value)}
            placeholder="22k / 18k / 925 / VS1"
            data-testid="ltc-scoped-purity"
          />
        </label>
        <label>
          <span>For</span>
          <select
            value={form.gender}
            onChange={(e) => update("gender", e.target.value)}
            data-testid="ltc-scoped-gender"
          >
            <option value="ladies">Ladies</option>
            <option value="men">Men</option>
            <option value="unisex">Unisex</option>
            <option value="kids">Kids</option>
          </select>
        </label>
      </div>

      <div className="admin-grid-2">
        <label>
          <span>Image 1 (main — shown on card)</span>
          <FileUpload
            value={form.image}
            onChange={(v) => update("image", v)}
            accept="image/*"
            data-testid="ltc-scoped-image"
          />
        </label>
        <label>
          <span>Image 2 (secondary — shown in gallery)</span>
          <FileUpload
            value={form.image2}
            onChange={(v) => update("image2", v)}
            accept="image/*"
            data-testid="ltc-scoped-image2"
          />
        </label>
      </div>

      <label>
        <span>Video (optional — 1 clip)</span>
        <FileUpload
          value={form.video}
          onChange={(v) => update("video", v)}
          accept="video/*"
          data-testid="ltc-scoped-video"
        />
      </label>

      <label>
        <span>Short description (optional)</span>
        <textarea
          rows={2}
          value={form.description}
          onChange={(e) => update("description", e.target.value)}
          placeholder="A hand-picked rakhi-day piece…"
          data-testid="ltc-scoped-desc"
        />
      </label>

      {err && <p className="admin-error" data-testid="ltc-scoped-error">{err}</p>}

      <div className="admin-actions">
        <button
          type="button"
          className="btn btn-primary"
          onClick={save}
          disabled={saving}
          data-testid="ltc-scoped-save"
        >
          {saving ? <Loader2 size={14} className="spin" /> : <Save size={14} />}
          <span style={{ marginLeft: 6 }}>Add to collection</span>
        </button>
      </div>
    </div>
  );
}
